import {
  categoriesQuery,
  categoryQuery,
  collectionQuery,
  collectionsQuery,
  featuredCollectionsQuery,
  homeQuery,
} from "./graphql/queries";
import { cdn, media, series } from "./util";

import _filter from "lodash/filter";
import _flattenDeep from "lodash/flattenDeep";
import _map from "lodash/map";
import _throttle from "lodash/throttle";
import _uniq from "lodash/uniq";
import { apolloClient } from "./apollo";

function addFilesFromCategory(query, slug) {
  return async (files = []) => {
    const {
      data: { category },
    } = await query(categoryQuery, { slug });
    if (!category) return files;
    return [
      ...files,
      media(category.icon, "icon"),
      media(category.background, "category-background"),
    ];
  };
}

function addFilesFromCollectionAndKind(query, slug, kind) {
  return async (files = []) => {
    const {
      data: { collection },
    } = await query(collectionQuery, {
      slug,
      kind,
    });
    if (!collection) return files;
    return [
      ...files,
      collection.details && media(collection.details, "original"),
      collection.listView && media(collection.listView, "collection-list"),
      collection.listView && media(collection.listView, "collection-featured"),
      collection.featuredView &&
        media(collection.featuredView, "collection-featured"),
      collection.preview && media(collection.preview, "collection-preview"),
      collection.orderDetails && media(collection.orderDetails, "original"),
      _map(collection.products, ({ variants }) =>
        _map(
          variants,
          (variant) => variant.image && media(variant.image, "original"),
        ),
      ),
    ];
  };
}

export default async function(loadingCallback = () => {}) {
  let loading = 0;
  let total = 0;
  let minTotal = 400;
  const updateLoading = _throttle(
    () =>
      loadingCallback({
        loaded: total - loading,
        total: Math.max(minTotal, total),
      }),
    100,
  );
  updateLoading();

  const query = async (query, variables = {}, count = 1) => {
    minTotal -= count;
    total += count;
    loading += count;
    updateLoading();
    const res = await apolloClient.query({
      query,
      variables,
    });
    loading -= count;
    updateLoading();
    return res;
  };

  total++;
  loading++;
  const allFiles = await series([
    () => [
      cdn("pdf/catalogue_01.4.jpg"),
      cdn("pdf/catalogue_99.3.jpg"),
      cdn("pdf/arrow.1.png"),
      cdn("pdf/logo-netexial.1.png"),
      cdn("fonts/Roboto-Regular.ttf"),
      cdn("fonts/Roboto-Italic.ttf"),
      cdn("fonts/Roboto-Light.ttf"),
      cdn("fonts/Roboto-Bold.ttf"),
      cdn("fonts/Roboto-Black.ttf"),
      cdn("fonts/Oswald-Light.ttf"),
      cdn("fonts/Oswald-Regular.ttf"),
      cdn("fonts/Oswald-Bold.ttf"),
    ],
    (files) => query(homeQuery).then(() => files),
    (files) => query(featuredCollectionsQuery).then(() => files),
    (files) =>
      query(categoriesQuery).then(async function({ data: { categories } }) {
        if (!categories) return files;
        const filesToAdd = await series(
          _map(categories.edges, ({ node: { slug } }) =>
            addFilesFromCategory(query, slug),
          ),
        );
        return [...files, filesToAdd];
      }),
    (files) =>
      query(collectionsQuery).then(async function({ data: { collections } }) {
        if (!collections) return files;
        const filesToAdd = await series(
          _map(
            collections.edges,
            ({ node: { slug } }) => async (filesForCollections = []) => {
              const filesCollectionToAdd = await series(
                _map(["clothing", "linen", "carpet", "accessory"], (kind) =>
                  addFilesFromCollectionAndKind(query, slug, kind),
                ),
              );
              return [...filesForCollections, filesCollectionToAdd];
            },
          ),
        );
        return [...files, filesToAdd];
      }),
  ]);
  minTotal = total;
  loading--;
  updateLoading();
  return _filter(_uniq(_flattenDeep(allFiles)));
}
